import React, { createContext, useContext, useEffect, useState } from 'react';
import { collection, updateDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { AuthContext } from './auth';
import { fireStore } from './firebase_context';


export const DatabaseContext = createContext({});

export const ChartOptions = Object.freeze({
    Last7Days: 7,
    Last30Days: 30,
    Lifetime: 100
  });

export const DatabaseProvider = (props) => {
    const {isAuthenticated, loggedInUser, reloadUser} = useContext(AuthContext);
    const [dailyNumbers, setDailyNumbers] = useState({
        numberArray: [],
        numberInfo: [],
        docId: null,
    });

    const [topScores, setTopScores] = useState({
        maxScore: 0,
        maxScoreHard: 0,
    });

    const [currentScores, setCurrentScores] = useState({
        current_score: 0,
        current_score_hard: 0,
        docId: null,
    });

    useEffect(() => {
        update();
    }, [loggedInUser]);

    useEffect(() => {
        const checkTopScores = async () => {
            if (isAuthenticated) {
                const {scores} = await getDailyNumbers();
                const userScores = await getCurrentScoresForUser();
                setTopScores({
                    maxScore: Math.max(scores.maxScore, userScores.current_score),
                    maxScoreHard: Math.max(scores.maxScoreHard, userScores.current_score_hard),
                });
            }
        }
        checkTopScores();
        const interval = setInterval(() => {
            checkTopScores();
        },5*1000);
        return () => clearInterval(interval);
    }, [isAuthenticated, dailyNumbers, currentScores]);

    async function update() {
        const {numbers, scores} = await getDailyNumbers();
        if (dailyNumbers.docId !== numbers.docId) {
            setDailyNumbers(numbers);
        }
        const userScores = await getCurrentScoresForUser();
        setTopScores({
            maxScore: Math.max(scores.maxScore, userScores.current_score),
            maxScoreHard: Math.max(scores.maxScoreHard, userScores.current_score_hard),
        });
        if (currentScores !== userScores){
            setCurrentScores(userScores);
        }
    }

    async function getDailyNumbers() {
        if (isAuthenticated) {
            const snapshot = await getDocs(query(collection(fireStore, 'daily_numbers'), where("is_active", "==", true)));
            if (snapshot.docs.length > 0) {
                const data = { ...snapshot.docs[0].data() };
                const docId = snapshot.docs[0].id;
                const gameData = {
                    numbers: {
                        numberArray: data.numbers.map((n, i) => n.value),
                        numberInfo: data.numbers,
                        docId: docId,
                    },
                    scores: {
                        maxScore: data.max_score,
                        maxScoreHard: data.max_score_hard,
                    }
                }
                return gameData;
            }
        } else {
            return {
                dailyNumbers: {
                    numberArray: [],
                    numberInfo: [],
                    docId: null,
                },
                topScores: {
                    maxScore: 0,
                    maxScoreHard: 0,
                }
            }
        }
    }

    async function getCurrentScoresForUser() {
        if (loggedInUser) {
            // fetch user & current game IDs
            const snapshot = await getDocs(query(collection(fireStore, 'users'), where("uid", "==", loggedInUser.uid)));
            if (snapshot.docs.length > 0) {
                // get or create game doc in subcollection based on current game id
                // NOTE: right now it's just the user doc
                
                const data = await snapshot.docs[0].data();
                const docId = snapshot.docs[0].id;
                
                // return current_score and current_score_hard
                return {
                    current_score: data.current_score,
                    current_score_hard: data.current_score_hard,
                    doc_id: docId                
                };
            }
        }
        
        return {
            current_score: 0,
            current_score_hard: 0,
            doc_id: null
        };
    }

    async function incrementScoreForUser(hardMode = false) {
        const d = hardMode ?
            {current_score_hard: currentScores.current_score_hard + 1} :
            {current_score: currentScores.current_score + 1}
        
        // Assumes this all lives on the user doc
        // SHOULD live on the same subcollection doc
        // used in the getCurrentScoresForUser function
        const snapshot = await getDocs(query(collection(fireStore, 'users'), where("uid", "==", loggedInUser.uid)));
        if (snapshot.docs.length > 0) {
            return await updateDoc(snapshot.docs[0].ref, d)
            .then(async () => {
                update();
                return true;
            })
            .catch((err) => {
                console.log(err);
                alert("Error updating score... try refreshing!");
                return false;
            });
        } else {
            console.log("Could not find user doc");
            alert("Error updating score... try refreshing!");
            return false;
        }
    }


    async function getUserStats(chartOption = ChartOptions.Last7Days) {
        if (isAuthenticated) {
            const gamesSnapshot = await getDocs(query(collection(fireStore, "daily_numbers"),  orderBy("date", "desc"), limit(chartOption + 1)));
            // streak
            // # of global max hits (normal)
            // # of global max hits (hard)
            // trends -> your max vs global max (normal and hard)
            //        -> last 7 days
            //        -> last 30 days
            //        -> lifetime
            
            let promises = gamesSnapshot.docs.map(async (d) => {
                const data = await d.data();
                const filteredGames = loggedInUser.previous_scores.filter((s)  => s.game_id === d.id);
                const gameData = data.is_active ? { max_score: currentScores.current_score, max_score_hard: currentScores.current_score_hard} :
                    filteredGames.length > 0 ? filteredGames[0] : {max_score: 0, max_score_hard: 0};
                return {
                    name: data.date,
                    user_score: gameData.max_score,
                    max_score: data.max_score,
                    user_score_hard: gameData.max_score_hard,
                    max_score_hard: data.max_score_hard
                }
            });

            return {
                streak: loggedInUser.streak,
                global_max_hits: loggedInUser.max_score_hits,
                global_max_hits_hard: loggedInUser.max_score_hits_hard,
                chart_data: (await Promise.all(promises)).reverse()
            };
        } else {
            return {
                streak: 0,
                global_max_hits: 0,
                global_max_hits_hard: 0,
                chartData: []
            }
        }
    }

    return (
        <DatabaseContext.Provider
            value={{
                dailyNumbers,
                currentScores,
                topScores,
                incrementScoreForUser,
                getUserStats,
            }}
        >
            {props.children}
        </DatabaseContext.Provider>
    );
};

export default DatabaseProvider;